import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table';

import { Loader2 } from 'lucide-react';

import useContactsApi, { type ContactInfoDto } from '@features/contacts/hooks/useContactsApi';

import type { ContactPreview } from '../types/wizard.types';
import { useOutreachViewModel } from '@features/contacts/hooks/useOutreachViewModel.ts';
import { type ApiResponse } from '@types';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { useHubspotViewModel } from '@features/contacts/hooks/useHubspotViewModel.ts';

interface ContactsPreviewModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contactListId: string | null;
  contactListTitle: string;
  activeTab?: string;
  salesforceNonce?: string;
  outreachContacts?: {
    nonce? : string; 
    stepId?: number; 
    sequenceId?: number
  };
}

export default function ContactsPreviewModal ({
  isOpen,
  onOpenChange,
  contactListId,
  contactListTitle,
  activeTab,
  salesforceNonce,
  outreachContacts
}: ContactsPreviewModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const { getContactsByContactListIdAsync } = useContactsApi();
  const { retrieveOutreachContacts } = useOutreachViewModel();
  const { retrieveSalesforceContacts } = useSalesforceViewModel();
  const { retrieveHubspotContacts } = useHubspotViewModel();

  const [contacts, setContacts] = useState<ContactPreview[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const selectContactsFetchCallback: (activeTab: string) => Promise<ContactInfoDto[] | undefined> = async () => {
    if (activeTab === 'outreach' && outreachContacts !== undefined) {
      const { nonce, stepId, sequenceId } = outreachContacts;
      return await retrieveOutreachContacts(nonce ?? '', stepId ?? 0, sequenceId ?? 0, true);
    }

    if (activeTab === 'hubspot' && contactListId !== null) {
      return await retrieveHubspotContacts(contactListId);
    }

    if (activeTab === 'salesforce' && contactListId !== null && salesforceNonce !== undefined) {
      return await retrieveSalesforceContacts(salesforceNonce, contactListId);
    }

    const response: ApiResponse<ContactInfoDto[]> = await getContactsByContactListIdAsync(contactListId ?? '');
    return response.data ?? [];
  };

  const getSourceLabel = ():string => {
    switch (activeTab) {
    case 'outreach': return 'Outreach';
    case 'salesforce': return 'Salesforce';
    case 'hubspot': return 'Hubspot';
    default: return 'CSV';
    }
  };

  useEffect(() => {
    const fetchContacts = async (): Promise<void> => {
      if (contactListId === null) {
        return;
      }

      setIsLoading(true);
      const result = await selectContactsFetchCallback(activeTab ?? '');
      
      if (result !== undefined) {
        setTotalContacts(result.length);
        const previewData: ContactPreview[] = result.slice(0).map((contact) => ({
          id: contact.id,
          name: contact.name,
          phone: contact.phone,
          accountName: contact.accountName,
          jobTitle: contact.jobTitle,
          email: contact.email,
          notes: contact.notes,
        }));
        setContacts(previewData);
      }
      setIsLoading(false);
    };

    if (isOpen) {
      void fetchContacts();
    }
  }, [isOpen, contactListId, activeTab, outreachContacts, salesforceNonce]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] rounded-2xl shadow-2xl bg-card text-foreground border border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t('wizard.contactsPreview.title')} · {contactListTitle}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {t('wizard.contactsPreview.empty')}
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] rounded-lg border border-border">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted/60">
                <TableRow className="border-b border-border/60 hover:bg-transparent">
                  <TableHead className="font-semibold">{t('wizard.contactsPreview.name')}</TableHead>
                  <TableHead className="font-semibold">{t('wizard.contactsPreview.company')}</TableHead>
                  <TableHead className="font-semibold">{t('wizard.contactsPreview.position')}</TableHead>
                  <TableHead className="font-semibold">{t('wizard.contactsPreview.phone')}</TableHead>
                  <TableHead className="font-semibold">{t('wizard.contactsPreview.notes')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} className="border-b border-border/40 hover:bg-muted/30">
                    <TableCell className="font-medium">{(contact.name ?? '') !== '' ? contact.name : '—'}</TableCell>
                    <TableCell>{(contact.accountName ?? '') !== '' ? contact.accountName : '—'}</TableCell>
                    <TableCell>{(contact.jobTitle ?? '') !== '' ? contact.jobTitle : '—'}</TableCell>
                    <TableCell>{(contact.phone ?? '') !== '' ? contact.phone : '—'}</TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {(contact.notes ?? '') !== '' ? contact.notes : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{t('wizard.contactsPreview.source')}: {getSourceLabel()}</span>
            <span>·</span>
            <span>{totalContacts} {t('wizard.summary.contactsCount')}</span>
          </div>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('wizard.contactsPreview.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
